
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { User, LogOut, Shield, Mail, Eye, Key } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { PasswordChangeDialog } from '@/components/employee/PasswordChangeDialog';

export function UserInfo() {
  const { user, supabaseUser, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast({
      title: "تم تسجيل الخروج",
      description: "نراك قريباً",
    });
    navigate('/login');
  };

  if (!user) {
    return (
      <Button 
        variant="outline" 
        onClick={() => navigate('/login')}
        className="gap-2"
      >
        <User className="w-4 h-4" />
        تسجيل الدخول
      </Button>
    );
  }

  // تحديد الصلاحيات للعرض
  const displayPermissions = user.permissions === 'ALL' 
    ? ['جميع الصلاحيات'] 
    : user.permissions;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Avatar className="w-6 h-6">
              {user.employeeData?.profile_image && <AvatarImage src={user.employeeData.profile_image} alt={user.username} />}
              <AvatarFallback className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white text-xs">
                {user.username.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            {user.isSupabaseUser ? <Mail className="w-4 h-4" /> : <User className="w-4 h-4" />}
            {user.username}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  {user.employeeData?.profile_image && <AvatarImage src={user.employeeData.profile_image} alt={user.username} />}
                  <AvatarFallback className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white">
                    {user.username.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span className="font-semibold">{user.role}</span>
                    {user.isSupabaseUser && (
                      <Badge variant="secondary" className="text-xs">حساب حقيقي</Badge>
                    )}
                    {!user.isSupabaseUser && (
                      <Badge variant="outline" className="text-xs">حساب تجريبي</Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    اسم المستخدم: {user.username}
                  </div>
                  {supabaseUser && (
                    <div className="text-sm text-gray-500">
                      البريد الإلكتروني: {supabaseUser.email}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="p-2">
            <div className="text-sm font-medium mb-2">الصلاحيات المتاحة:</div>
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
              {displayPermissions.map((permission: string) => (
                <Badge key={permission} variant="secondary" className="text-xs">
                  {permission}
                </Badge>
              ))}
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/my-profile')} className="gap-2">
            <Eye className="w-4 h-4" />
            عرض الملف الشخصي
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPasswordDialogOpen(true)} className="gap-2">
            <Key className="w-4 h-4" />
            تغيير كلمة المرور
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-red-600 gap-2">
            <LogOut className="w-4 h-4" />
            تسجيل الخروج
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <PasswordChangeDialog 
        open={passwordDialogOpen} 
        onOpenChange={setPasswordDialogOpen}
        employeeId={user.employeeData?.id}
      />
    </>
  );
}
